import { useEffect, useRef, useState } from "react";

const BUTTON = {
  A: 0,
  B: 1,
  SELECT: 8,
  START: 9,
  DPAD_UP: 12,
  DPAD_DOWN: 13,
  DPAD_LEFT: 14,
  DPAD_RIGHT: 15,
};

const AXIS_DEADZONE = 0.5;
const STICK_REPEAT_MS = 160;
const DPAD_REPEAT_MS = 140;
const INITIAL_REPEAT_MS = 300;
const AXIS = { LX: 0, LY: 1 };
const CURSOR_ATTR = "data-gamepad-cursor";
const REGION_ATTR = "data-gamepad-region";
const SELECT_BACKDROP_ATTR = "data-gamepad-select-backdrop";
const SELECT_MENU_ATTR = "data-gamepad-select-menu";
const SELECT_OPTION_ATTR = "data-gamepad-select-option";
const KEYBOARD_BACKDROP_ATTR = "data-gamepad-keyboard-backdrop";
const KEYBOARD_ATTR = "data-gamepad-keyboard";
const KEYBOARD_KEY_ATTR = "data-gamepad-keyboard-key";

let activeGamepadSelectEl = null;
let activeGamepadKeyboardField = null;
let clearActiveKeyboardFieldRef = null;

const KEYBOARD_ALPHA_ROWS = [
  ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
  ["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
  ["a", "s", "d", "f", "g", "h", "j", "k", "l"],
  ["shift", "z", "x", "c", "v", "b", "n", "m", "back"],
  ["123", "space", "clear", "done"],
];

const KEYBOARD_SYMBOL_ROWS = [
  ["!", "@", "#", "$", "%", "^", "&", "*", "(", ")"],
  ["-", "_", "=", "+", "[", "]", "{", "}", "\\", "|"],
  [";", ":", '"', "'", ",", ".", "<", ">", "/", "?"],
  ["abc", "@", ".", "back"],
  ["space", "clear", "done"],
];

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  "a[href]",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[role="button"]:not([aria-disabled="true"])',
  '[tabindex]:not([tabindex="-1"])',
  "[data-gamepad-item]",
].join(",");

const ZONE_ATTR = "data-gamepad-zone";

function isVisible(el) {
  if (!el || !el.isConnected) return false;
  if (el.getAttribute("aria-hidden") === "true") return false;
  const style = window.getComputedStyle(el);
  if (style.display === "none" || style.visibility === "hidden") return false;
  if (Number(style.opacity) === 0) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function isEditableTarget(el) {
  if (!el || typeof el.closest !== "function") return false;
  const editable = el.closest(
    'input:not([type="button"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]):not([type="range"]), textarea, [contenteditable="true"]',
  );
  return Boolean(editable);
}

function isEditableField(el) {
  if (!el || !el.tagName) return false;
  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (el.isContentEditable) return true;
  if (tag !== "INPUT") return false;
  const type = (el.getAttribute("type") || "text").toLowerCase();
  return ![
    "button",
    "submit",
    "checkbox",
    "radio",
    "range",
    "file",
    "reset",
    "image",
    "hidden",
  ].includes(type);
}

function blurEditableFields(root) {
  const active = document.activeElement;
  if (active && isEditableField(active)) {
    active.blur();
  }
  if (!root) return;
  root
    .querySelectorAll(
      "input:focus, textarea:focus, [contenteditable='true']:focus",
    )
    .forEach((node) => {
      if (typeof node.blur === "function") node.blur();
    });
}

function focusShell(root) {
  if (!root || typeof root.focus !== "function") return;
  try {
    root.focus({ preventScroll: true });
  } catch (_) {
    root.focus();
  }
}

function lockFieldFromSystemKeyboard(field) {
  if (!field || !(field.tagName === "INPUT" || field.tagName === "TEXTAREA")) {
    return;
  }
  if (!Object.prototype.hasOwnProperty.call(field.dataset, "gamepadInputMode")) {
    field.dataset.gamepadInputMode = field.getAttribute("inputmode") || "";
  }
  if (!Object.prototype.hasOwnProperty.call(field.dataset, "gamepadReadOnly")) {
    field.dataset.gamepadReadOnly = field.readOnly ? "1" : "0";
  }
  field.setAttribute("inputmode", "none");
  field.readOnly = true;
  try {
    field.blur();
  } catch (_) {
    /* ignore */
  }
}

function unlockFieldFromSystemKeyboard(field) {
  if (!field || !field.dataset) return;
  if (Object.prototype.hasOwnProperty.call(field.dataset, "gamepadReadOnly")) {
    field.readOnly = field.dataset.gamepadReadOnly === "1";
    delete field.dataset.gamepadReadOnly;
  }
  if (Object.prototype.hasOwnProperty.call(field.dataset, "gamepadInputMode")) {
    const saved = field.dataset.gamepadInputMode;
    if (saved) field.setAttribute("inputmode", saved);
    else field.removeAttribute("inputmode");
    delete field.dataset.gamepadInputMode;
  }
}

function getFieldText(field) {
  if (!field) return "";
  if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
    return String(field.value || "");
  }
  if (field.isContentEditable) return String(field.textContent || "");
  return "";
}

function setFieldText(field, text) {
  if (!field) return;
  const next = String(text);
  if (field.tagName === "INPUT" || field.tagName === "TEXTAREA") {
    const previous = field.value;
    const proto =
      field.tagName === "TEXTAREA"
        ? window.HTMLTextAreaElement.prototype
        : window.HTMLInputElement.prototype;
    const descriptor = Object.getOwnPropertyDescriptor(proto, "value");
    if (descriptor && typeof descriptor.set === "function") {
      descriptor.set.call(field, next);
    } else {
      field.value = next;
    }
    const tracker = field._valueTracker;
    if (tracker && typeof tracker.setValue === "function") {
      tracker.setValue(previous);
    }
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
    return;
  }
  if (field.isContentEditable) {
    field.textContent = next;
    field.dispatchEvent(new Event("input", { bubbles: true }));
  }
}

function getGamepadKeyboard(root) {
  if (!root) return null;
  const keyboard = root.querySelector(`[${KEYBOARD_ATTR}]`);
  return keyboard && isVisible(keyboard) ? keyboard : null;
}

function closeGamepadKeyboard(root) {
  const field = activeGamepadKeyboardField;
  activeGamepadKeyboardField = null;
  if (typeof clearActiveKeyboardFieldRef === "function") {
    clearActiveKeyboardFieldRef();
    clearActiveKeyboardFieldRef = null;
  }
  if (field) unlockFieldFromSystemKeyboard(field);
  if (!root) return field;
  root.querySelectorAll(`[${KEYBOARD_BACKDROP_ATTR}]`).forEach((node) => {
    node.remove();
  });
  return field;
}

function exitGamepadKeyboard(root) {
  if (!getGamepadKeyboard(root) && !activeGamepadKeyboardField) return null;
  const field = closeGamepadKeyboard(root);
  focusShell(root);
  if (field && root?.contains(field)) {
    setCursor(root, field);
    return field;
  }
  return true;
}

function updateGamepadKeyboardPreview(menu, field) {
  if (!menu) return;
  const preview = menu.querySelector("[data-gamepad-keyboard-preview]");
  if (!preview) return;
  const text = getFieldText(field);
  preview.textContent = text || " ";
  preview.setAttribute("data-empty", text ? "0" : "1");
}

function keyboardKeyLabel(key, shift) {
  if (key === "shift") return shift ? "SHIFT" : "Shift";
  if (key === "back") return "⌫";
  if (key === "space") return "Space";
  if (key === "done") return "Done";
  if (key === "clear") return "Clear";
  if (key === "123") return "123";
  if (key === "abc") return "ABC";
  if (key.length === 1 && /[a-z]/.test(key)) {
    return shift ? key.toUpperCase() : key;
  }
  return key;
}

function handleGamepadKeyboardKey(root, menu, key) {
  const field = activeGamepadKeyboardField;
  if (!field || !menu) return;

  if (key === "done") {
    exitGamepadKeyboard(root);
    return;
  }
  if (key === "shift") {
    const next = menu.getAttribute("data-shift") === "1" ? "0" : "1";
    menu.setAttribute("data-shift", next);
    rebuildGamepadKeyboardKeys(root, menu, "shift");
    return;
  }
  if (key === "123") {
    menu.setAttribute("data-symbols", "1");
    menu.setAttribute("data-shift", "0");
    rebuildGamepadKeyboardKeys(root, menu, "123");
    return;
  }
  if (key === "abc") {
    menu.setAttribute("data-symbols", "0");
    menu.setAttribute("data-shift", "0");
    rebuildGamepadKeyboardKeys(root, menu, "abc");
    return;
  }

  const shift = menu.getAttribute("data-shift") === "1";
  let text = getFieldText(field);
  if (key === "back") {
    text = text.slice(0, -1);
  } else if (key === "clear") {
    text = "";
  } else if (key === "space") {
    text += " ";
  } else if (key.length === 1) {
    text += shift && /[a-z]/.test(key) ? key.toUpperCase() : key;
    if (shift && /[a-z]/.test(key)) {
      menu.setAttribute("data-shift", "0");
      rebuildGamepadKeyboardKeys(root, menu, key);
      setFieldText(field, text);
      updateGamepadKeyboardPreview(menu, field);
      return;
    }
  } else {
    text += key;
  }
  setFieldText(field, text);
  updateGamepadKeyboardPreview(menu, field);
}

function rebuildGamepadKeyboardKeys(root, menu, preferKey = null) {
  const keysHost = menu.querySelector("[data-gamepad-keyboard-keys]");
  if (!keysHost) return;
  const shift = menu.getAttribute("data-shift") === "1";
  const symbols = menu.getAttribute("data-symbols") === "1";
  const rows = symbols ? KEYBOARD_SYMBOL_ROWS : KEYBOARD_ALPHA_ROWS;
  keysHost.innerHTML = "";

  rows.forEach((row) => {
    const rowEl = document.createElement("div");
    rowEl.setAttribute("data-gamepad-keyboard-row", "");
    row.forEach((key) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.setAttribute(KEYBOARD_KEY_ATTR, "");
      btn.dataset.key = key;
      if (key === "space") btn.setAttribute("data-key-wide", "space");
      if (key === "done") btn.setAttribute("data-key-wide", "done");
      if (key === "shift" && shift) btn.setAttribute("data-key-active", "");
      if ((key === "123" && symbols) || (key === "abc" && !symbols)) {
        btn.setAttribute("data-key-active", "");
      }
      btn.textContent = keyboardKeyLabel(key, shift);
      btn.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleGamepadKeyboardKey(root, menu, key);
      });
      rowEl.appendChild(btn);
    });
    keysHost.appendChild(rowEl);
  });

  const preferred =
    (preferKey &&
      keysHost.querySelector(
        `[data-key="${String(preferKey).replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"]`,
      )) ||
    keysHost.querySelector(`[${KEYBOARD_KEY_ATTR}]`);
  if (preferred) setCursor(root, preferred);
}

function openGamepadKeyboard(root, field, keyboardFieldRef = null) {
  if (!root || !isEditableField(field)) return null;
  closeGamepadKeyboard(root);
  closeGamepadSelectMenu(root);
  blurEditableFields(root);
  lockFieldFromSystemKeyboard(field);
  focusShell(root);

  activeGamepadKeyboardField = field;
  if (keyboardFieldRef) {
    keyboardFieldRef.current = field;
    clearActiveKeyboardFieldRef = () => {
      keyboardFieldRef.current = null;
    };
  }

  const backdrop = document.createElement("div");
  backdrop.setAttribute(KEYBOARD_BACKDROP_ATTR, "");
  const menu = document.createElement("div");
  menu.setAttribute(KEYBOARD_ATTR, "");
  menu.setAttribute("data-shift", "0");
  menu.setAttribute("data-symbols", "0");
  menu.setAttribute("role", "group");
  menu.setAttribute("aria-label", "On-screen keyboard");

  const header = document.createElement("div");
  header.setAttribute("data-gamepad-keyboard-header", "");
  const title = document.createElement("div");
  title.setAttribute("data-gamepad-keyboard-title", "");
  title.textContent =
    field.getAttribute("aria-label") ||
    field.getAttribute("placeholder") ||
    (field.labels && field.labels[0] ? field.labels[0].textContent : "") ||
    "Text";
  header.appendChild(title);
  const preview = document.createElement("div");
  preview.setAttribute("data-gamepad-keyboard-preview", "");
  header.appendChild(preview);
  menu.appendChild(header);

  const keysHost = document.createElement("div");
  keysHost.setAttribute("data-gamepad-keyboard-keys", "");
  menu.appendChild(keysHost);

  const hint = document.createElement("div");
  hint.setAttribute("data-gamepad-keyboard-hint", "");
  hint.textContent = "A: type · B: close · Select/Start: close";
  menu.appendChild(hint);

  backdrop.appendChild(menu);
  backdrop.addEventListener("click", (event) => {
    if (event.target !== backdrop) return;
    exitGamepadKeyboard(root);
  });
  root.appendChild(backdrop);

  updateGamepadKeyboardPreview(menu, field);
  rebuildGamepadKeyboardKeys(root, menu, "a");
  return getGamepadKeyboard(root);
}

function centerOf(el) {
  const rect = el.getBoundingClientRect();
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
    rect,
  };
}

function collectFocusables(scope, { ignoreSkip = false, labelsOnly = false } = {}) {
  if (!scope) return [];
  if (labelsOnly) {
    return Array.from(
      scope.querySelectorAll("[data-gamepad-setting-label]"),
    ).filter((el) => {
      if (!isVisible(el)) return false;
      if (el.disabled) return false;
      return true;
    });
  }
  return Array.from(scope.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => {
    if (!isVisible(el)) return false;
    if (el.disabled) return false;
    if (el.getAttribute("aria-disabled") === "true") return false;
    const tabIndex = el.getAttribute("tabindex");
    if (tabIndex === "-1") return false;
    if (!ignoreSkip && el.hasAttribute("data-gamepad-skip")) return false;
    return true;
  });
}

function sortReadingOrder(els) {
  return els.slice().sort((a, b) => {
    const ar = a.getBoundingClientRect();
    const br = b.getBoundingClientRect();
    const rowDelta = ar.top - br.top;
    if (Math.abs(rowDelta) > 12) return rowDelta;
    return ar.left - br.left;
  });
}

function resolveOverlayRoot(root, overlayClassNames) {
  if (!root) return null;
  const names = (overlayClassNames || []).filter(Boolean);
  if (!names.length) return null;
  let top = null;
  for (const className of names) {
    const nodes = root.getElementsByClassName(className);
    for (let i = 0; i < nodes.length; i += 1) {
      const node = nodes[i];
      if (!isVisible(node)) continue;
      const style = window.getComputedStyle(node);
      const rect = node.getBoundingClientRect();
      const coversViewport =
        style.position === "fixed" &&
        rect.width >= Math.min(window.innerWidth, 240) * 0.85 &&
        rect.height >= Math.min(window.innerHeight, 240) * 0.5;
      if (!coversViewport) continue;
      top = node;
    }
  }
  return top;
}

function getRegionEl(root, name) {
  if (!root) return null;
  const el = root.querySelector(`[${REGION_ATTR}="${name}"]`);
  return el && isVisible(el) ? el : null;
}

function clearCursor(root) {
  if (!root) return;
  root.querySelectorAll(`[${CURSOR_ATTR}]`).forEach((node) => {
    node.removeAttribute(CURSOR_ATTR);
  });
}

function isScrollableOverflow(value) {
  return value === "auto" || value === "scroll" || value === "overlay";
}

function getFullscreenElement() {
  if (typeof document === "undefined") return null;
  return document.fullscreenElement || document.webkitFullscreenElement || null;
}

function canScrollVertically(node) {
  if (!node) return false;
  return node.scrollHeight > node.clientHeight + 1;
}

function canScrollHorizontally(node) {
  if (!node) return false;
  return node.scrollWidth > node.clientWidth + 1;
}

function collectScrollParents(el) {
  const parents = [];
  const fullscreenEl = getFullscreenElement();
  let node = el?.parentElement || null;
  while (node && node !== document.documentElement) {
    const style = window.getComputedStyle(node);
    const isFs = fullscreenEl && node === fullscreenEl;
    const canY =
      canScrollVertically(node) &&
      (isFs || isScrollableOverflow(style.overflowY));
    const canX =
      canScrollHorizontally(node) &&
      (isFs || isScrollableOverflow(style.overflowX));
    if (canY || canX) parents.push(node);
    node = node.parentElement;
  }

  if (
    fullscreenEl &&
    el &&
    fullscreenEl.contains(el) &&
    !parents.includes(fullscreenEl) &&
    canScrollVertically(fullscreenEl)
  ) {
    parents.push(fullscreenEl);
  }

  if (!fullscreenEl) {
    const scroller = document.scrollingElement || document.documentElement;
    if (scroller && !parents.includes(scroller)) parents.push(scroller);
  }
  return parents;
}

function scrollAxisIntoView(parent, elRect, parentRect, axis, margin) {
  if (axis === "y") {
    const topGap = elRect.top - parentRect.top;
    const bottomGap = parentRect.bottom - elRect.bottom;
    if (topGap < margin) {
      parent.scrollTop -= margin - topGap;
    } else if (bottomGap < margin) {
      parent.scrollTop += margin - bottomGap;
    }
    return;
  }
  const leftGap = elRect.left - parentRect.left;
  const rightGap = parentRect.right - elRect.right;
  if (leftGap < margin) {
    parent.scrollLeft -= margin - leftGap;
  } else if (rightGap < margin) {
    parent.scrollLeft += margin - rightGap;
  }
}

function getScrollerViewportRect(parent) {
  const fullscreenEl = getFullscreenElement();
  const isRootScroller =
    parent === document.scrollingElement ||
    parent === document.documentElement ||
    parent === document.body;
  if (isRootScroller && !fullscreenEl) {
    return {
      top: 0,
      left: 0,
      right: window.innerWidth,
      bottom: window.innerHeight,
      width: window.innerWidth,
      height: window.innerHeight,
    };
  }
  return parent.getBoundingClientRect();
}

function scrollParentBy(parent, dx, dy) {
  const fullscreenEl = getFullscreenElement();
  const isRootScroller =
    !fullscreenEl &&
    (parent === document.scrollingElement ||
      parent === document.documentElement ||
      parent === document.body);
  if (isRootScroller) {
    window.scrollBy(dx, dy);
    return;
  }
  if (dx) parent.scrollLeft += dx;
  if (dy) parent.scrollTop += dy;
}

function ensureGamepadTargetVisible(el, direction = null, margin = 48) {
  if (!el || typeof el.getBoundingClientRect !== "function") return;

  const parents = collectScrollParents(el);
  for (let i = 0; i < parents.length; i += 1) {
    const parent = parents[i];
    const style = window.getComputedStyle(parent);
    const fullscreenEl = getFullscreenElement();
    const isFs = fullscreenEl && parent === fullscreenEl;
    const isRootScroller =
      !fullscreenEl &&
      (parent === document.scrollingElement ||
        parent === document.documentElement ||
        parent === document.body);
    const parentRect = getScrollerViewportRect(parent);
    const elRect = el.getBoundingClientRect();
    const canY =
      canScrollVertically(parent) &&
      (isFs ||
        isRootScroller ||
        isScrollableOverflow(style.overflowY));
    const canX =
      canScrollHorizontally(parent) &&
      (isFs || isScrollableOverflow(style.overflowX));

    if (canY) {
      const topGap = elRect.top - parentRect.top;
      const bottomGap = parentRect.bottom - elRect.bottom;
      const needsScroll = topGap < margin || bottomGap < margin;
      if (needsScroll) {
        if (direction === "down" || direction === "up") {
          const ideal =
            direction === "down"
              ? elRect.bottom - (parentRect.bottom - margin)
              : elRect.top - (parentRect.top + margin);
          scrollParentBy(parent, 0, ideal);
        } else {
          scrollAxisIntoView(parent, elRect, parentRect, "y", margin);
        }
      }
    }

    if (canX) {
      const refreshed = el.getBoundingClientRect();
      const leftGap = refreshed.left - parentRect.left;
      const rightGap = parentRect.right - refreshed.right;
      if (leftGap < margin || rightGap < margin) {
        scrollAxisIntoView(parent, refreshed, parentRect, "x", margin);
      }
    }
  }

  const finalRect = el.getBoundingClientRect();
  const viewTop = margin;
  const viewBottom = window.innerHeight - margin;
  const fullscreenEl = getFullscreenElement();
  if (fullscreenEl && fullscreenEl.contains(el)) {
    const fsRect = fullscreenEl.getBoundingClientRect();
    const topGap = finalRect.top - fsRect.top;
    const bottomGap = fsRect.bottom - finalRect.bottom;
    if (topGap < margin) {
      fullscreenEl.scrollTop -= margin - topGap;
    } else if (bottomGap < margin) {
      fullscreenEl.scrollTop += margin - bottomGap;
    }
    return;
  }
  if (finalRect.top < viewTop) {
    window.scrollBy(0, finalRect.top - viewTop);
  } else if (finalRect.bottom > viewBottom) {
    window.scrollBy(0, finalRect.bottom - viewBottom);
  }
}

function setCursor(root, el, direction = null) {
  if (!root || !el) return null;
  clearCursor(root);
  el.setAttribute(CURSOR_ATTR, "");

  if (isEditableField(el)) {
    blurEditableFields(root);
    focusShell(root);
  } else {
    const active = document.activeElement;
    if (active && isEditableField(active)) {
      active.blur();
    }
    if (typeof el.focus === "function") {
      try {
        el.focus({ preventScroll: true });
      } catch (_) {
        el.focus();
      }
    }
  }

  ensureGamepadTargetVisible(el, direction);
  return el;
}

function getCursorEl(root) {
  if (!root) return null;
  return root.querySelector(`[${CURSOR_ATTR}]`);
}

function scoreCandidate(from, candidate, direction) {
  const a = centerOf(from);
  const b = centerOf(candidate);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  const overlapX =
    Math.min(a.rect.right, b.rect.right) - Math.max(a.rect.left, b.rect.left);
  const overlapY =
    Math.min(a.rect.bottom, b.rect.bottom) - Math.max(a.rect.top, b.rect.top);

  let primary = 0;
  let secondary = 0;
  let aligned = false;

  if (direction === "left") {
    if (dx >= -2) return null;
    primary = -dx;
    secondary = absDy;
    aligned = overlapY > 0 || absDy < a.rect.height;
  } else if (direction === "right") {
    if (dx <= 2) return null;
    primary = dx;
    secondary = absDy;
    aligned = overlapY > 0 || absDy < a.rect.height;
  } else if (direction === "up") {
    if (dy >= -2) return null;
    primary = -dy;
    secondary = absDx;
    aligned = overlapX > 0 || absDx < a.rect.width;
  } else if (direction === "down") {
    if (dy <= 2) return null;
    primary = dy;
    secondary = absDx;
    aligned = overlapX > 0 || absDx < a.rect.width;
  } else {
    return null;
  }

  const isVertical = direction === "up" || direction === "down";
  const rejectRatio = isVertical ? 3.2 : 1.45;
  if (!aligned && secondary > primary * rejectRatio) return null;
  const alignPenalty = aligned ? 0 : secondary * (isVertical ? 1.1 : 2.5);
  return primary + secondary * (isVertical ? 0.2 : 0.35) + alignPenalty;
}

function scoreCandidateLoose(from, candidate, direction) {
  const a = centerOf(from);
  const b = centerOf(candidate);
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  if (direction === "left") {
    if (dx >= -2) return null;
    return -dx + absDy * 0.15;
  }
  if (direction === "right") {
    if (dx <= 2) return null;
    return dx + absDy * 0.15;
  }
  if (direction === "up") {
    if (dy >= -2) return null;
    return -dy + absDx * 0.12;
  }
  if (direction === "down") {
    if (dy <= 2) return null;
    return dy + absDx * 0.12;
  }
  return null;
}

function findSpatialNeighborLoose(current, candidates, direction) {
  if (!candidates.length) return null;
  if (!current || !candidates.includes(current)) {
    return sortReadingOrder(candidates)[0] || null;
  }
  let best = null;
  let bestScore = Infinity;
  for (const candidate of candidates) {
    if (candidate === current) continue;
    const score = scoreCandidateLoose(current, candidate, direction);
    if (score == null || score >= bestScore) continue;
    bestScore = score;
    best = candidate;
  }
  return best;
}

function getZoneName(el) {
  if (!el || typeof el.closest !== "function") return null;
  return el.closest(`[${ZONE_ATTR}]`)?.getAttribute(ZONE_ATTR) || null;
}

function findZoneNeighbor(current, candidates, direction) {
  if (!current || !candidates.length) return null;
  const currentZone = getZoneName(current);
  if (!currentZone) return null;
  const outside = candidates.filter((el) => {
    if (el === current) return false;
    const zone = getZoneName(el);
    return zone && zone !== currentZone;
  });
  if (!outside.length) return null;
  return (
    findSpatialNeighbor(current, outside, direction) ||
    findSpatialNeighborLoose(current, outside, direction)
  );
}

function findSpatialNeighbor(current, candidates, direction) {
  if (!candidates.length) return null;
  if (!current || !candidates.includes(current)) {
    return sortReadingOrder(candidates)[0] || null;
  }
  let best = null;
  let bestScore = Infinity;
  for (const candidate of candidates) {
    if (candidate === current) continue;
    const score = scoreCandidate(current, candidate, direction);
    if (score == null || score >= bestScore) continue;
    bestScore = score;
    best = candidate;
  }
  return best;
}

function findLinearNeighbor(current, candidates, direction) {
  const ordered = sortReadingOrder(candidates);
  if (!ordered.length) return null;
  if (!current || !ordered.includes(current)) {
    return direction === "up" ? ordered[ordered.length - 1] : ordered[0];
  }
  const index = ordered.indexOf(current);
  if (direction === "up") {
    return ordered[Math.max(0, index - 1)];
  }
  if (direction === "down") {
    return ordered[Math.min(ordered.length - 1, index + 1)];
  }
  return findSpatialNeighbor(current, ordered, direction);
}

function preferLinearNav(el) {
  if (!el || typeof el.closest !== "function") return false;
  if (el.hasAttribute("data-gamepad-setting-label")) return true;
  if (el.closest("[data-gamepad-settings]")) return true;
  if (el.closest("[data-gamepad-options-active]")) return true;
  if (el.closest(`[${SELECT_MENU_ATTR}]`)) return true;
  return false;
}

function findNeighbor(current, candidates, direction) {
  if (current?.closest?.(`[${KEYBOARD_ATTR}]`)) {
    return (
      findSpatialNeighbor(current, candidates, direction) ||
      findLinearNeighbor(current, candidates, direction)
    );
  }
  if (preferLinearNav(current) && (direction === "up" || direction === "down")) {
    return findLinearNeighbor(current, candidates, direction);
  }
  return (
    findSpatialNeighbor(current, candidates, direction) ||
    findZoneNeighbor(current, candidates, direction) ||
    findSpatialNeighborLoose(current, candidates, direction) ||
    findLinearNeighbor(current, candidates, direction)
  );
}

function activateElement(el) {
  if (!el) return;
  const tag = el.tagName;
  const type = (el.getAttribute("type") || "").toLowerCase();
  if (
    tag === "INPUT" &&
    (type === "checkbox" || type === "radio" || type === "range")
  ) {
    el.click();
    return;
  }
  el.click();
}

function applySelectValue(selectEl, value) {
  if (!selectEl) return;
  const previous = selectEl.value;
  const descriptor = Object.getOwnPropertyDescriptor(
    window.HTMLSelectElement.prototype,
    "value",
  );
  if (descriptor && typeof descriptor.set === "function") {
    descriptor.set.call(selectEl, value);
  } else {
    selectEl.value = value;
  }
  if (selectEl.value !== value) {
    const match = Array.from(selectEl.options || []).find(
      (opt) => opt.value === value,
    );
    if (match) match.selected = true;
  }
  const tracker = selectEl._valueTracker;
  if (tracker && typeof tracker.setValue === "function") {
    tracker.setValue(previous);
  }
  selectEl.dispatchEvent(new Event("input", { bubbles: true }));
  selectEl.dispatchEvent(new Event("change", { bubbles: true }));
}

function getGamepadSelectMenu(root) {
  if (!root) return null;
  const menu = root.querySelector(`[${SELECT_MENU_ATTR}]`);
  return menu && isVisible(menu) ? menu : null;
}

function closeGamepadSelectMenu(root) {
  activeGamepadSelectEl = null;
  if (!root) return;
  root.querySelectorAll(`[${SELECT_BACKDROP_ATTR}]`).forEach((node) => {
    node.remove();
  });
}

function exitGamepadSelectMenu(root) {
  if (!getGamepadSelectMenu(root)) return null;
  const selectEl = activeGamepadSelectEl;
  closeGamepadSelectMenu(root);
  if (selectEl && root?.contains(selectEl)) {
    setCursor(root, selectEl);
    return selectEl;
  }
  return true;
}

function openGamepadSelectMenu(root, selectEl) {
  if (!root || !selectEl || selectEl.tagName !== "SELECT") return null;
  closeGamepadSelectMenu(root);
  blurEditableFields(root);

  const options = Array.from(selectEl.options || []).filter(
    (opt) => !opt.disabled && opt.hidden !== true,
  );
  if (!options.length) return null;

  activeGamepadSelectEl = selectEl;
  const backdrop = document.createElement("div");
  backdrop.setAttribute(SELECT_BACKDROP_ATTR, "");
  const menu = document.createElement("div");
  menu.setAttribute(SELECT_MENU_ATTR, "");
  menu.setAttribute("role", "listbox");

  const title = document.createElement("div");
  title.setAttribute("data-gamepad-select-title", "");
  const labelText =
    selectEl.getAttribute("aria-label") ||
    (selectEl.labels && selectEl.labels[0]
      ? selectEl.labels[0].textContent
      : "") ||
    "Select";
  title.textContent = String(labelText).trim() || "Select";
  menu.appendChild(title);

  const list = document.createElement("div");
  list.setAttribute("data-gamepad-select-list", "");

  let selectedBtn = null;
  options.forEach((opt) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute("role", "option");
    btn.setAttribute(SELECT_OPTION_ATTR, "");
    btn.textContent = opt.label || opt.textContent || opt.value;
    const isCurrent = opt.value === selectEl.value || opt.selected;
    if (isCurrent) {
      btn.setAttribute("aria-selected", "true");
      btn.setAttribute("data-gamepad-select-current", "");
      selectedBtn = btn;
    } else {
      btn.setAttribute("aria-selected", "false");
    }
    btn.addEventListener("click", (event) => {
      event.preventDefault();
      event.stopPropagation();
      applySelectValue(selectEl, opt.value);
      closeGamepadSelectMenu(root);
      setCursor(root, selectEl);
    });
    list.appendChild(btn);
  });

  menu.appendChild(list);
  backdrop.appendChild(menu);
  backdrop.addEventListener("click", (event) => {
    if (event.target !== backdrop) return;
    exitGamepadSelectMenu(root);
  });
  root.appendChild(backdrop);

  const focusTarget = selectedBtn || list.querySelector("button");
  return setCursor(root, focusTarget);
}

function readDigitalDirection(pad) {
  const buttons = pad.buttons || [];
  const axes = pad.axes || [];
  if (buttons[BUTTON.DPAD_UP]?.pressed) return "up";
  if (buttons[BUTTON.DPAD_DOWN]?.pressed) return "down";
  if (buttons[BUTTON.DPAD_LEFT]?.pressed) return "left";
  if (buttons[BUTTON.DPAD_RIGHT]?.pressed) return "right";
  const lx = axes[AXIS.LX] || 0;
  const ly = axes[AXIS.LY] || 0;
  if (Math.abs(lx) < AXIS_DEADZONE && Math.abs(ly) < AXIS_DEADZONE) return null;
  if (Math.abs(lx) > Math.abs(ly)) {
    return lx > 0 ? "right" : "left";
  }
  return ly > 0 ? "down" : "up";
}

function pickActivePad() {
  if (typeof navigator === "undefined" || !navigator.getGamepads) return null;
  const pads = navigator.getGamepads();
  if (!pads) return null;
  for (let i = 0; i < pads.length; i += 1) {
    const pad = pads[i];
    if (pad && pad.connected) return pad;
  }
  return null;
}

function resolveActiveRegion(root, preferred) {
  const nav = getRegionEl(root, "nav");
  const main = getRegionEl(root, "main");
  if (preferred === "nav" && nav) return { kind: "nav", el: nav };
  if (preferred === "main" && main) return { kind: "main", el: main };
  if (main) return { kind: "main", el: main };
  if (nav) return { kind: "nav", el: nav };
  return { kind: "root", el: root };
}

function clearSettingsOptionsMode(root) {
  if (!root) return;
  root.querySelectorAll("[data-gamepad-options-active]").forEach((node) => {
    node.removeAttribute("data-gamepad-options-active");
  });
  root.querySelectorAll("[data-gamepad-setting-open]").forEach((node) => {
    node.removeAttribute("data-gamepad-setting-open");
  });
}

function getActiveSettingsOptions(scope) {
  if (!scope) return null;
  const active = scope.querySelector("[data-gamepad-options-active]");
  return active && isVisible(active) ? active : null;
}

function resolveNavScope(root, regionName, overlayClassNames) {
  const keyboard = getGamepadKeyboard(root);
  if (keyboard) {
    return {
      el: keyboard,
      ignoreSkip: false,
      labelsOnly: false,
      overlay: true,
    };
  }
  const selectMenu = getGamepadSelectMenu(root);
  if (selectMenu) {
    return {
      el: selectMenu,
      ignoreSkip: false,
      labelsOnly: false,
      overlay: true,
    };
  }
  const overlay = resolveOverlayRoot(root, overlayClassNames);
  if (overlay) {
    return { el: overlay, ignoreSkip: false, labelsOnly: false, overlay: true };
  }
  const resolved = resolveActiveRegion(root, regionName);
  const activeOptions = getActiveSettingsOptions(resolved.el);
  if (activeOptions) {
    return {
      el: activeOptions,
      ignoreSkip: true,
      labelsOnly: false,
      overlay: false,
    };
  }
  const settings = resolved.el?.querySelector?.("[data-gamepad-settings]");
  if (settings && isVisible(settings)) {
    return {
      el: settings,
      ignoreSkip: false,
      labelsOnly: true,
      overlay: false,
    };
  }
  return {
    el: resolved.el,
    ignoreSkip: false,
    labelsOnly: false,
    overlay: false,
  };
}

function collectScopeFocusables(scopeInfo) {
  return collectFocusables(scopeInfo.el, {
    ignoreSkip: scopeInfo.ignoreSkip,
    labelsOnly: scopeInfo.labelsOnly,
  });
}

function enterSettingsOptions(root, labelEl) {
  if (!root || !labelEl) return null;
  const block = labelEl.closest("[data-gamepad-setting]");
  if (!block) return null;
  const options = block.querySelector("[data-gamepad-setting-options]");
  if (!options) return null;
  const buttons = collectFocusables(options, { ignoreSkip: true });
  if (!buttons.length) return null;
  clearSettingsOptionsMode(root);
  options.setAttribute("data-gamepad-options-active", "");
  labelEl.setAttribute("data-gamepad-setting-open", "");
  const preferred =
    buttons.find((btn) => btn.getAttribute("aria-pressed") === "true") ||
    buttons.find((btn) =>
      (btn.className || "").includes("buttonPrimary"),
    ) ||
    buttons[0];
  return setCursor(root, preferred);
}

function exitSettingsOptions(root) {
  if (!root) return null;
  const active = getActiveSettingsOptions(root);
  if (!active) return null;
  const block = active.closest("[data-gamepad-setting]");
  const label = block?.querySelector("[data-gamepad-setting-label]") || null;
  clearSettingsOptionsMode(root);
  if (label) setCursor(root, label);
  return label;
}

function activateGamepadTarget(root, cursor, keyboardFieldRef) {
  if (!root || !cursor) return;
  if (cursor.hasAttribute("data-gamepad-setting-label")) {
    const entered = enterSettingsOptions(root, cursor);
    if (entered) return;
    return;
  }
  if (cursor.tagName === "SELECT") {
    openGamepadSelectMenu(root, cursor);
    return;
  }
  if (isEditableField(cursor)) {
    openGamepadKeyboard(root, cursor, keyboardFieldRef);
    return;
  }
  const inOptions = cursor.closest("[data-gamepad-options-active]");
  activateElement(cursor);
  if (inOptions) {
    window.requestAnimationFrame(() => {
      exitSettingsOptions(root);
    });
  }
}

export default function useGamepadNavigation({
  rootRef,
  enabled = true,
  overlayClassNames = [],
  onBack,
  onEnterNavRegion,
  depsKey = "",
}) {
  const [connected, setConnected] = useState(false);
  const [padId, setPadId] = useState(null);
  const [activeRegion, setActiveRegion] = useState("main");
  const connectedRef = useRef(false);
  const regionRef = useRef("main");
  const keyboardFieldRef = useRef(null);
  const pressedRef = useRef({ a: false, b: false, select: false, start: false });
  const holdRef = useRef({
    direction: null,
    since: 0,
    lastFire: 0,
    source: null,
  });
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;
  const onEnterNavRegionRef = useRef(onEnterNavRegion);
  onEnterNavRegionRef.current = onEnterNavRegion;
  const overlayClassNamesRef = useRef(overlayClassNames);
  overlayClassNamesRef.current = overlayClassNames;

  useEffect(() => {
    regionRef.current = activeRegion;
  }, [activeRegion]);

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return undefined;

    const closeKeyboardFromField = () => {
      const root = rootRef?.current;
      if (!getGamepadKeyboard(root) && !activeGamepadKeyboardField) {
        keyboardFieldRef.current = null;
        return false;
      }
      exitGamepadKeyboard(root);
      keyboardFieldRef.current = null;
      return true;
    };

    const onKeyDown = (event) => {
      if (event.key !== "Escape" && event.key !== "GoBack") return;
      const root = rootRef?.current;
      if (getGamepadKeyboard(root) || activeGamepadKeyboardField) {
        event.preventDefault();
        event.stopPropagation();
        closeKeyboardFromField();
        return;
      }
      if (getGamepadSelectMenu(root)) {
        event.preventDefault();
        event.stopPropagation();
        exitGamepadSelectMenu(root);
      }
    };

    const onPointerDown = (event) => {
      const field = event.target?.closest?.(
        "input, textarea, [contenteditable='true']",
      );
      if (!field || !isEditableField(field)) return;
      if (getGamepadKeyboard(rootRef?.current)) {
        event.preventDefault();
        return;
      }
      unlockFieldFromSystemKeyboard(field);
    };

    window.addEventListener("keydown", onKeyDown, true);
    document.addEventListener("pointerdown", onPointerDown, true);

    const syncConnection = () => {
      const pad = pickActivePad();
      const next = Boolean(pad);
      connectedRef.current = next;
      setConnected(next);
      setPadId(pad ? pad.id || "Controller" : null);
      if (!next) {
        keyboardFieldRef.current = null;
        clearActiveKeyboardFieldRef = null;
        closeGamepadKeyboard(rootRef?.current);
        closeGamepadSelectMenu(rootRef?.current);
        clearCursor(rootRef?.current);
      }
      return pad;
    };

    const focusInRegion = (regionName, preferredEl = null) => {
      const root = rootRef?.current;
      if (!root) return null;
      const scopeInfo = resolveNavScope(
        root,
        regionName,
        overlayClassNamesRef.current,
      );
      const focusables = collectScopeFocusables(scopeInfo);
      const ordered = sortReadingOrder(focusables);
      if (!ordered.length) {
        clearCursor(root);
        return null;
      }
      const cursor = getCursorEl(root);
      const target =
        (preferredEl && ordered.includes(preferredEl) && preferredEl) ||
        (cursor && ordered.includes(cursor) && cursor) ||
        ordered[0];
      if (!scopeInfo.overlay) {
        regionRef.current = regionName === "nav" ? "nav" : "main";
        setActiveRegion(regionRef.current);
      }
      return setCursor(root, target);
    };

    const toggleRegion = () => {
      const root = rootRef?.current;
      if (!root) return;
      if (getGamepadKeyboard(root) || activeGamepadKeyboardField) {
        closeKeyboardFromField();
        return;
      }
      if (getGamepadSelectMenu(root)) {
        exitGamepadSelectMenu(root);
        return;
      }
      if (resolveOverlayRoot(root, overlayClassNamesRef.current)) return;
      clearSettingsOptionsMode(root);
      const next = regionRef.current === "nav" ? "main" : "nav";
      regionRef.current = next;
      setActiveRegion(next);
      if (next === "nav" && typeof onEnterNavRegionRef.current === "function") {
        onEnterNavRegionRef.current();
      }
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          focusInRegion(next);
        });
      });
    };

    const onConnect = () => {
      syncConnection();
    };
    const onDisconnect = () => {
      syncConnection();
    };

    window.addEventListener("gamepadconnected", onConnect);
    window.addEventListener("gamepaddisconnected", onDisconnect);
    syncConnection();

    let raf = 0;
    const tick = (now) => {
      raf = window.requestAnimationFrame(tick);
      const pad = pickActivePad();
      const isConnected = Boolean(pad);
      if (isConnected !== connectedRef.current) {
        connectedRef.current = isConnected;
        setConnected(isConnected);
        setPadId(pad ? pad.id || "Controller" : null);
        if (!isConnected) clearCursor(rootRef?.current);
      }
      if (!pad) {
        pressedRef.current = { a: false, b: false, select: false, start: false };
        holdRef.current = {
          direction: null,
          since: 0,
          lastFire: 0,
          source: null,
        };
        return;
      }

      const root = rootRef?.current;
      if (!root) return;

      const activeEl = document.activeElement;
      const editing =
        Boolean(getGamepadKeyboard(root)) ||
        Boolean(activeGamepadKeyboardField);
      const aPressed = Boolean(pad.buttons?.[BUTTON.A]?.pressed);
      const bPressed = Boolean(
        pad.buttons?.[BUTTON.B]?.pressed ||
          (pad.buttons?.[BUTTON.B]?.value || 0) > 0.5,
      );
      const selectPressed = Boolean(
        pad.buttons?.[BUTTON.SELECT]?.pressed ||
          (pad.buttons?.[BUTTON.SELECT]?.value || 0) > 0.5,
      );
      const startPressed = Boolean(
        pad.buttons?.[BUTTON.START]?.pressed ||
          (pad.buttons?.[BUTTON.START]?.value || 0) > 0.5,
      );
      const direction = readDigitalDirection(pad);

      if (selectPressed && !pressedRef.current.select) {
        if (editing) {
          closeKeyboardFromField();
        } else {
          toggleRegion();
        }
      }
      if (startPressed && !pressedRef.current.start) {
        if (editing) {
          closeKeyboardFromField();
        } else {
          toggleRegion();
        }
      }

      if (aPressed && !pressedRef.current.a) {
        const cursor =
          getCursorEl(root) || focusInRegion(regionRef.current, activeEl);
        activateGamepadTarget(root, cursor, keyboardFieldRef);
      }
      if (bPressed && !pressedRef.current.b) {
        if (editing) {
          closeKeyboardFromField();
        } else if (exitGamepadSelectMenu(root)) {
          // closed dropdown
        } else if (exitSettingsOptions(root)) {
          // returned to setting label
        } else if (typeof onBackRef.current === "function") {
          onBackRef.current();
        }
      }
      pressedRef.current = {
        a: aPressed,
        b: bPressed,
        select: selectPressed,
        start: startPressed,
      };

      if (!direction) {
        holdRef.current = {
          direction: null,
          since: 0,
          lastFire: 0,
          source: null,
        };
        return;
      }

      const hold = holdRef.current;
      const isStick = !(
        pad.buttons?.[BUTTON.DPAD_UP]?.pressed ||
        pad.buttons?.[BUTTON.DPAD_DOWN]?.pressed ||
        pad.buttons?.[BUTTON.DPAD_LEFT]?.pressed ||
        pad.buttons?.[BUTTON.DPAD_RIGHT]?.pressed
      );
      const repeatMs = isStick ? STICK_REPEAT_MS : DPAD_REPEAT_MS;
      let shouldFire = false;
      if (hold.direction !== direction) {
        hold.direction = direction;
        hold.since = now;
        hold.lastFire = now;
        hold.source = isStick ? "stick" : "dpad";
        shouldFire = true;
      } else {
        const elapsed = now - hold.since;
        const sinceFire = now - hold.lastFire;
        if (elapsed >= INITIAL_REPEAT_MS && sinceFire >= repeatMs) {
          hold.lastFire = now;
          shouldFire = true;
        }
      }

      if (!shouldFire) return;

      const scopeInfo = resolveNavScope(
        root,
        regionRef.current,
        overlayClassNamesRef.current,
      );
      const focusables = collectScopeFocusables(scopeInfo);
      if (!focusables.length) return;
      let current = getCursorEl(root);
      if (!current || !focusables.includes(current)) {
        current = setCursor(root, sortReadingOrder(focusables)[0]);
      }
      const next = findNeighbor(current, focusables, direction);
      if (!next || next === current) return;
      setCursor(root, next, direction);
    };

    raf = window.requestAnimationFrame(tick);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKeyDown, true);
      document.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("gamepadconnected", onConnect);
      window.removeEventListener("gamepaddisconnected", onDisconnect);
      keyboardFieldRef.current = null;
      closeGamepadKeyboard(rootRef?.current);
      closeGamepadSelectMenu(rootRef?.current);
      clearSettingsOptionsMode(rootRef?.current);
      clearCursor(rootRef?.current);
    };
  }, [enabled, rootRef]);

  useEffect(() => {
    if (!enabled || !connected) return;
    const root = rootRef?.current;
    if (!root) return;
    if (getGamepadKeyboard(root) || getGamepadSelectMenu(root)) return;
    clearSettingsOptionsMode(root);
    const scopeInfo = resolveNavScope(
      root,
      regionRef.current,
      overlayClassNames,
    );
    const ordered = sortReadingOrder(collectScopeFocusables(scopeInfo));
    const cursor = getCursorEl(root);
    const target =
      (cursor && ordered.includes(cursor) && cursor) || ordered[0] || null;
    setCursor(root, target);
  }, [enabled, connected, depsKey, rootRef, overlayClassNames]);

  return { connected, padId, activeRegion };
}
